import { useAuthStore } from '../stores/authStore';

/**
 * Erro lançado quando a API responde com status >= 400.
 * Carrega o status HTTP, código de erro do backend (se vier) e mensagem.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Faz uma requisição HTTP, injetando o JWT do authStore se disponível.
 * Lança ApiError se status >= 400.
 */
async function request<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((init.headers as Record<string, string>) || {}),
  };

  // Injeta JWT se autenticado
  const token = useAuthStore.getState().token;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Body JSON automático se for objeto
  let body = init.body;
  if (body && typeof body !== 'string' && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  }

  const response = await fetch(path, { ...init, headers, body });

  // Tenta parsear o body como JSON
  let parsed: unknown = null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      parsed = await response.json();
    } catch {
      // body inválido — segue como null
    }
  }

  if (!response.ok) {
    const errBody = parsed as { error?: string; code?: string; details?: unknown } | null;
    throw new ApiError(
      response.status,
      errBody?.error || `HTTP ${response.status}`,
      errBody?.code,
      errBody?.details
    );
  }

  return parsed as T;
}

/**
 * GET autenticado.
 */
export function apiGet<T = unknown>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}

/**
 * POST autenticado com body JSON.
 */
export function apiPost<T = unknown>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: body as BodyInit | null,
  });
}

/**
 * PATCH autenticado com body JSON.
 */
export function apiPatch<T = unknown>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PATCH',
    body: body as BodyInit | null,
  });
}

/**
 * DELETE autenticado.
 */
export function apiDelete<T = unknown>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}
